import { useQueryClient } from '@tanstack/react-query'
import { BadgeDollarSign, Globe, LifeBuoy, LogOut, Monitor, Moon, Settings, Shield, Sun, SunMoon, User } from 'lucide-react'
import { useNavigate } from 'react-router'
import {
  Avatar,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui'
import { track } from '@/lib/analytics'
import { saveUserPreference, useAuth, useLogout } from '@/lib/auth'
import { HAS_LANGUAGE_CHOICE, LANGUAGES, setLanguage, t, useI18n } from '@/lib/i18n'
import { setTheme, useTheme, type ThemePreference } from '@/lib/theme'

// Labels are literal t() calls (run at render), so the theme names reach the translators.
const THEMES: Array<{ value: ThemePreference; label: () => string; icon: typeof Sun }> = [
  { value: 'light', label: () => t('Light'), icon: Sun },
  { value: 'dark', label: () => t('Dark'), icon: Moon },
  { value: 'system', label: () => t('System'), icon: Monitor },
]

/** Avatar menu: profile, settings, affiliates, theme, language, help, log out (UI.md section 3). */
export function UserMenu({ communitySlug }: { communitySlug: string | null }) {
  const { user, isPlatformAdmin } = useAuth()
  const { preference } = useTheme()
  const { lang } = useI18n()
  const logout = useLogout()
  const qc = useQueryClient()
  const navigate = useNavigate()
  if (!user) return null

  const onTheme = (value: string) => {
    const pref = value as ThemePreference
    track('theme_changed', { theme: pref, surface: 'user_menu' })
    setTheme(pref)
    void saveUserPreference(qc, { theme: pref })
  }
  const onLanguage = (value: string) => {
    track('language_changed', { from: lang, to: value, surface: 'user_menu' })
    void setLanguage(value)
    void saveUserPreference(qc, { language: value })
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        aria-label={t('Account menu')}
        className="focus-ring grid size-10 shrink-0 place-items-center rounded-full transition-opacity duration-150 hover:opacity-90"
      >
        <Avatar name={user.name} src={user.avatarUrl} id={user.id} size={32} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="w-64">
        <DropdownMenuLabel className="px-2.5 py-2">
          <span className="block truncate text-body font-semibold text-text">{user.name}</span>
          <span className="block truncate text-meta font-normal text-muted">@{user.handle}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem icon={<User />} onSelect={() => navigate(`/u/${user.handle}`)}>
          {t('Profile')}
        </DropdownMenuItem>
        <DropdownMenuItem icon={<Settings />} onSelect={() => navigate('/settings')}>
          {t('Settings')}
        </DropdownMenuItem>
        {communitySlug ? (
          <DropdownMenuItem icon={<BadgeDollarSign />} onSelect={() => navigate(`/${communitySlug}/affiliates`)}>
            {t('Affiliates')}
          </DropdownMenuItem>
        ) : null}
        {isPlatformAdmin ? (
          <DropdownMenuItem icon={<Shield />} onSelect={() => navigate('/admin')}>
            {t('Admin console')}
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger icon={<SunMoon />}>{t('Theme')}</DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuRadioGroup value={preference} onValueChange={onTheme}>
              {THEMES.map(({ value, label, icon: Icon }) => (
                <DropdownMenuRadioItem key={value} value={value}>
                  <span className="flex items-center gap-2.5">
                    <Icon size={16} strokeWidth={1.75} className="text-muted" aria-hidden="true" />
                    {label()}
                  </span>
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        {HAS_LANGUAGE_CHOICE && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger icon={<Globe />}>{t('Language')}</DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="max-h-[min(420px,var(--radix-dropdown-menu-content-available-height))] overflow-y-auto">
              <DropdownMenuRadioGroup value={lang} onValueChange={onLanguage}>
                {LANGUAGES.map((l) => (
                  <DropdownMenuRadioItem key={l.code} value={l.code} lang={l.code}>
                    {l.name}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}
        <DropdownMenuItem icon={<LifeBuoy />} onSelect={() => navigate('/support')}>
          {t('Help')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem icon={<LogOut />} onSelect={() => logout.mutate()}>
          {t('Log out')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
